import { useEffect } from 'react';

interface MetaOptions {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  price?: number;
  noindex?: boolean;
  /** Override the canonical path (defaults to the current pathname). Use this on
   *  pages driven by query params (search, filters) so every param combination
   *  consolidates to one canonical URL instead of being treated as a separate page. */
  canonicalPath?: string;
}

const SITE_NAME = 'Aiphra';
const DEFAULT_DESC = 'The AI agent marketplace built for performance. Buy and sell autonomous AI agents.';
const BASE_URL = 'https://aiphra.com';

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}

function setOG(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export function usePageMeta(options: MetaOptions = {}) {
  const { title, description = DEFAULT_DESC, image, type = 'website', price, noindex, canonicalPath } = options;

  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Buy and Sell AI Agents`;

    document.title = fullTitle;

    setMeta('description', description);
    if (noindex) setMeta('robots', 'noindex,nofollow');
    else setMeta('robots', 'index,follow');

    setOG('og:title', fullTitle);
    setOG('og:description', description);
    setOG('og:type', type);
    setOG('og:site_name', SITE_NAME);
    if (image) setOG('og:image', image);
    if (price !== undefined) {
      setOG('product:price:amount', String(price));
      setOG('product:price:currency', 'USD');
    }

    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (image) setMeta('twitter:image', image);

    setLink('canonical', `${BASE_URL}${canonicalPath ?? window.location.pathname}`);

    return () => {
      document.title = `${SITE_NAME} — Buy and Sell AI Agents`;
    };
  }, [title, description, image, type, price, noindex, canonicalPath]);
}
